import { Modal, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { CommonModal } from '@sketch-note/design-system/src/components/modal';
import { StandardSnack } from '@sketch-note/design-system/src/components/snackbar';
import { Input } from '@sketch-note/design-system/src/components/text-input/text-input.component';
import { Member } from '@sketch-note/design-system/src/components/workspace-settings/workspace-settings';
import { ErrorIcon } from '@sketchnote/icons';
import { Avatar, Button, ComingSoonIconOnly, LoaderSm } from '@sketchnote/shared-components';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { API, endpoints } from 'src/api';
import { useLoaderStore } from 'src/components/loaders';
import { isAdvUser } from 'src/constants/plans';
import { useLayout } from 'src/shared/hooks/use-layout';

import { useAccountStore, useManageMembersStore, useSettings } from '../store';
import { LogoUpload } from './logo-upload';
import { PictureUpload } from './picture-upload';

export interface WorkspaceProps {
  name: string;
  members: Member[];
}

export const Account = ({
  onClose,
  plan,
  trial,
}: {
  onClose: () => void;
  plan: string;
  trial: boolean;
}) => {
  const {
    organization,
    setOrganization,
    organizationImage,
    setOrganizationImage,
    organisationImageSize,

    organisationLogoSize,
    organizationLogo,
  } = useAccountStore((state) => state);

  const { admin, isOnlyAdmin } = useManageMembersStore((state) => state);

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [leaveModalIsOpen, setLeaveModalIsOpen] = useState(false);
  const [loaderIsOpen, setLoaderIsOpen] = useState(false);
  const [snackbarModalIsOpen, setSnackbarModalIsOpen] = useState(false);
  const [callbacks] = useSettings();
  const [errorSnackbarModalIsOpen, setErrorSnackbarModalIsOpen] = useState(false);
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const { onLogout } = useLayout();
  const { setIsLoading, isLoading } = useLoaderStore();
  const handleLoaderModal = () => {
    setLoaderIsOpen(false);
  };
  const handleErrorSnackbarModal = () => {
    setErrorSnackbarModalIsOpen(false);
  };

  const handleDeleteOrganization = async (value) => {
    setIsLoading(true);

    const response = await callbacks?.onDeleteOrganisation?.({ challenge: value });
    if (response?.success) {
      setIsLoading(false);
      handleLogOut();
    } else {
      setIsLoading(false);
    }
    setDeleteModalIsOpen(false);
  };
  const handleSnackbarModal = () => {
    setSnackbarModalIsOpen(false);
  };
  const handleDeleteModalClose = () => {
    setDeleteModalIsOpen(false);
  };
  const handleDeleteModalOpen = () => {
    setDeleteModalIsOpen(true);
  };
  const handleLeaveOrganisation = async () => {
    setIsLoading(true);
    const response = await API.delete(endpoints.settings.leaveOrganisation.url);
    if (response.data.success) {
      setIsLoading(false);
      location.reload();
    }
  };
  const handleLeaveModalClose = () => {
    setLeaveModalIsOpen(false);
  };
  const handleLeaveModalOpen = () => {
    setLeaveModalIsOpen(true);
  };
  const handleLogOut = async () => {
    onLogout();
  };

  const handleSaveChanges = async () => {
    if (organisationImageSize > 3145728 || organisationLogoSize > 3145728) {
      setErrorSnackbarModalIsOpen(true);
      setTimeout(function () {
        setErrorSnackbarModalIsOpen(false);
      }, 1500);
    } else {
      setLoaderIsOpen(true);

      if (organizationImage instanceof File) {
        const responseImage = await callbacks?.onUpdateOrganisationImage?.(organizationImage);

        if (responseImage?.isSuccess) {
          setLoaderIsOpen(false);
          setSnackbarModalIsOpen(true);
          setTimeout(function () {
            setSnackbarModalIsOpen(false);
          }, 1500);
        }
      }
      if (organizationLogo instanceof File || organizationLogo === 'remove_logo') {
        const responseImage = await callbacks?.onUpdateOrganisationLogo?.(organizationLogo);

        if (responseImage?.isSuccess) {
          setLoaderIsOpen(false);
          setSnackbarModalIsOpen(true);
          setTimeout(function () {
            setSnackbarModalIsOpen(false);
          }, 1500);
        }
      }
      const responseName = await callbacks?.onUpdateOrganisationName?.({ name: organization });
      if (responseName?.isSuccess) {
        setLoaderIsOpen(false);
        setSnackbarModalIsOpen(true);
        setTimeout(function () {
          setSnackbarModalIsOpen(false);
        }, 1500);
      }
    }
  };

  return (
    <div>
      <div className="overflow-y-auto px-48" style={{ height: '52rem' }}>
        {admin && (
          <section className="border-b border-gray-200 pt-16 pb-32">
            <span className="mb-32 block font-medium">Overview</span>
            {admin && (
              <PictureUpload
                onImageChange={(imageURL) => {
                  setOrganizationImage(imageURL);
                }}
              />
            )}
            {admin && (
              <span className="mb-8 text-lg block  text-gray-600 mt-32">
                <Input
                  wrapperClassName="settings-input pt-7 pb-7 px-12 bg-gray-100"
                  label="Organization name"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                />
              </span>
            )}
          </section>
        )}
        {!admin && (
          <section className="border-b border-gray-200 pt-16 pb-32">
            <span className="mb-32 block font-medium">Overview</span>
            <div className="flex">
              <Avatar
                name={organization}
                size="xl"
                type="photo"
                wrapperClassName="border"
                src={organizationImage}
              />
              <span className="ml-22 mt-18">{organization}</span>
            </div>
          </section>
        )}

        <section className="border-b border-gray-200 py-16">
          <span className="mb-16 font-medium text-sm">White Labeling</span>
          {isAdvUser(plan) && !trial ? (
            <></>
          ) : (
            <span className="text-blue-500 bg-blue-100 px-6 py-4 rounded-lg text-xs font-medium inline-block ml-4">
              💙 Advanced plan
            </span>
          )}
          <div className="text-gray-500 text-sm my-10">
            <span>Customize Sketchnote&apos;s look and feel for your company.</span>
            {isAdvUser(plan) && !trial ? (
              <LogoUpload admin={admin} />
            ) : (
              <div>
                <span className="block my-8">
                  White labeling is only available on Sketchnote Advanced. Upgrade to Advanced to
                  unlock this feature.
                </span>
                <button
                  onClick={() => {
                    onClose();
                    router.push(`/${mode}/${orgId}/pricing`);
                  }}
                  className="bg-blue-500 px-8 py-4 rounded-sm text-white text-sm"
                >
                  Upgrade now
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="py-32">
          <span className="mb-8 block font-medium text-gray-600">Delete organization</span>
          <p className="text-sm mb-22 text-gray-600 w-400">
            Files and lesson progress in this organization will be deleted for everyone. This action
            can&apos;t be reversed
          </p>

          {!isOnlyAdmin && (
            <Button className="mr-12" status="secondary" onClick={handleLeaveModalOpen}>
              Leave
            </Button>
          )}
          {admin && (
            //onClick={handleDeleteModalOpen}
            <Button disabled={true} status="danger">
              Delete organization&nbsp;
              <ComingSoonIconOnly />
            </Button>
          )}

          <DeleteOrganization
            isOpen={deleteModalIsOpen}
            handleOnClose={handleDeleteModalClose}
            handleOnClick={(value) => handleDeleteOrganization(value)}
          />

          <LeaveOrg
            handleOnClose={handleLeaveModalClose}
            isOpen={leaveModalIsOpen}
            handleLeave={handleLeaveOrganisation}
          />
        </section>
      </div>
      <div className="sticky flex justify-end mt-10 px-64 py-12 border-t border-gray-200 ">
        <Button status="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button status="primary" className="ml-10" onClick={handleSaveChanges}>
          Save changes
        </Button>
      </div>
      <Modal isOpen={loaderIsOpen} onClose={handleLoaderModal}>
        <ModalOverlay />
        <ModalContent style={{ width: '38.4rem', padding: '30px', marginTop: '20rem' }}>
          <p className="font-semibold mb-20"> We&apos;re saving your changes </p>
          <LoaderSm className="mx-auto text-xss" />
        </ModalContent>
      </Modal>

      <Modal isOpen={snackbarModalIsOpen} onClose={handleSnackbarModal}>
        <ModalContent style={{ width: '180px', borderRadius: '10px', marginTop: '65rem' }}>
          <StandardSnack text="Changes Saved" />
        </ModalContent>
      </Modal>
      <Modal isOpen={errorSnackbarModalIsOpen} onClose={handleErrorSnackbarModal}>
        <ModalContent style={{ width: '40rem', borderRadius: '10px', marginTop: '65rem' }}>
          <StandardSnack icon={<ErrorIcon />} text="Oops! File too large, try again" />
        </ModalContent>
      </Modal>
    </div>
  );
};

const LeaveOrg = ({
  isOpen,
  handleOnClose,
  handleLeave,
}: {
  isOpen: boolean;
  handleOnClose: () => void;
  handleLeave: () => void;
}) => {
  return (
    <CommonModal
      isOpen={isOpen}
      headText="Leave organization"
      footerMainLabel="Leave organization"
      footerSecLabel="Cancel"
      onClose={handleOnClose}
      onMainClick={handleLeave}
      onSecClick={handleOnClose}
      contentStyle={{ width: '460px' }}
      footerButtonStatus="danger"
    >
      <p className="text-sm mb-22 text-gray-600">
        You&apos;ll lose access to the workspaces you were in{' '}
      </p>
    </CommonModal>
  );
};

const DeleteOrganization = ({
  isOpen,
  handleOnClose,
  handleOnClick,
}: {
  isOpen: boolean;
  handleOnClose: () => void;
  handleOnClick: (value) => void;
}) => {
  const [challenge, setChallenge] = useState('');

  return (
    <CommonModal
      isOpen={isOpen}
      headText="Delete organization?"
      footerMainLabel="Confirm"
      footerSecLabel="Cancel"
      onClose={handleOnClose}
      onMainClick={() => handleOnClick(challenge)}
      onSecClick={handleOnClose}
      contentStyle={{ width: '460px' }}
      footerButtonStatus="danger"
    >
      <div>
        <p className="text-base mb-32 text-gray-600 font-regular">
          This will permanently delete the workspaces and its content. This action cannot be undone.
          <br />
          <br />
          Please type the name of the organization to confirm
        </p>
        <Input
          required={true}
          wrapperClassName="bg-gray-100 pt-7 pb-7 px-12 "
          placeholder="Organization name"
          onChange={(e) => setChallenge(e.target.value)}
        />
      </div>
    </CommonModal>
  );
};
